import { useCallback, useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import {
  EdmDocumentationLinkedTasksListQueryVariables,
  Task,
  TaskFilterInputObject,
  TaskScopeInputObject,
  TaskScopeNameEnum,
  useDashboardTaskIdsLazyQuery,
  useEdmDocumentationLinkedTasksListQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

type UseEdmDocumentationLinkedTasksListQueryHookProps = ListQueryHookProps & {
  scope?: TaskScopeInputObject;
};

const DEFAULT_SEARCH_SCHEMA = [
  { fieldName: 'name' },
  { fieldName: 'formTemplateName' },
  { fieldName: 'formCategoryName' },
  { fieldName: 'siteName' },
  { fieldName: 'siteAreaName' },
  { fieldName: 'siteCode' },
  { fieldName: 'siteAreaCode' },
  { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
];

// Doc View → Operational Scope → Tasks tab. Server-side paginated/sorted/
// searched list of tasks linked to a documentation. Callers should pass
// `scope: { name: TaskScopeNameEnum.Current }` so BE collapses recurring
// chains to one row per group_key (matches the doc form's groupKey-keyed
// Tasks field).
export default function useEdmDocumentationLinkedTasksListQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  onCompleted,
  onError,
  orderingParams,
  paginationParams,
  scope,
  scopeName,
  searchQuery,
  searchQueryFilterSchema,
  skip,
  usePrevious = true,
}: UseEdmDocumentationLinkedTasksListQueryHookProps): ListQueryHookResult<Task> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );

  const resolvedScope: TaskScopeInputObject = useMemo(
    () =>
      scope ?? {
        name: (scopeName as TaskScopeNameEnum) ?? TaskScopeNameEnum.Current,
      },
    [scope, scopeName],
  );

  const pageNumber = Number(page);
  const limitNumber = Number(perPage);
  const variables: EdmDocumentationLinkedTasksListQueryVariables = {
    scope: resolvedScope,
    page: Number.isNaN(pageNumber) ? undefined : pageNumber,
    limit: Number.isNaN(limitNumber) ? undefined : limitNumber,
  };

  const filters: TaskFilterInputObject[] = [];

  if (searchQuery) {
    const schema =
      searchQueryFilterSchema && searchQueryFilterSchema.length
        ? searchQueryFilterSchema.map((fieldName) => ({ fieldName }))
        : DEFAULT_SEARCH_SCHEMA;
    filters.push(...createSearchFilters(searchQuery, schema));
  }

  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }

  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useEdmDocumentationLinkedTasksListQuery({
      fetchPolicy,
      variables,
      skip,
    });

  useEffect(() => {
    if (data && onCompleted) onCompleted();
  }, [data, onCompleted]);

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  const [fetchIdsQuery] = useDashboardTaskIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          onlyIds: true,
          filters: variables.filters,
          scope: resolvedScope,
        },
      }),
    [fetchIdsQuery, variables.filters, resolvedScope],
  );

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'tasks.collection', []) as Task[]);
  const metadata = _get(queryData, 'tasks.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
